import React, { useState, useEffect } from 'react';
import './blackboard-integration.css';

/**
 * BlackboardIntegration component for connecting and deploying models to Blackboard Learn LMS
 * 
 * This component provides a UI for:
 * 1. Connecting to a Blackboard Learn instance with REST API credentials
 * 2. Selecting courses to deploy models to
 * 3. Configuring model deployment options
 * 4. Deploying models to Blackboard courses
 */
const BlackboardIntegration = ({ user = null, onDeploy = null }) => {
  // State for connection configuration
  const [domain, setDomain] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('');
  const [connectorId, setConnectorId] = useState(null);
  
  // State for course selection and deployment
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [contentItems, setContentItems] = useState([]);
  const [selectedContentItems, setSelectedContentItems] = useState([]);
  const [deploymentStatus, setDeploymentStatus] = useState('idle'); // idle, deploying, success, error
  const [deploymentOptions, setDeploymentOptions] = useState({
    enableGrading: true,
    adaptToLearningStyles: true,
    enableBiasReporting: true,
    enableTokenEconomy: true
  });
  
  // State for saved configurations
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [loadingSavedConfigs, setLoadingSavedConfigs] = useState(false);

  // Load saved configurations on component mount
  useEffect(() => {
    loadSavedConfigurations();
  }, []);

  // Load any previously saved Blackboard configurations
  const loadSavedConfigurations = async () => {
    try {
      setLoadingSavedConfigs(true);
      const response = await fetch('/api/blackboard/configs');
      const data = await response.json();
      
      if (data.success && data.configs) {
        setSavedConfigs(data.configs);
        
        // If there's a saved configuration, pre-fill the form
        if (data.configs.length > 0) {
          setDomain(data.configs[0].domain || '');
          setApiKey(data.configs[0].api_key || '');
          setApiSecret(data.configs[0].api_secret || '');
        }
      }
    } catch (error) {
      console.error('Error loading saved Blackboard configurations:', error);
    } finally {
      setLoadingSavedConfigs(false);
    }
  };

  // Save current configuration for later use
  const saveConfiguration = async () => {
    if (!domain || !apiKey || !apiSecret) {
      setConnectionStatus('Domain, API Key, and API Secret are required');
      return;
    }

    try {
      const response = await fetch('/api/blackboard/save-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          domain: domain,
          api_key: apiKey,
          api_secret: apiSecret
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus('Configuration saved successfully');
        // Reload saved configurations
        loadSavedConfigurations();
      } else {
        setConnectionStatus(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setConnectionStatus('Error saving configuration');
    }
  };

  // Connect to Blackboard with the provided credentials
  const connectToBlackboard = async () => {
    if (!domain || !apiKey || !apiSecret) {
      setConnectionStatus('Domain, API Key, and API Secret are required');
      return;
    }

    try {
      setConnectionStatus('Connecting to Blackboard...');
      
      const response = await fetch('/api/blackboard/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          domain: domain,
          api_key: apiKey,
          api_secret: apiSecret,
          demo_mode: false // Set to true for testing
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus('Connected to Blackboard successfully');
        setConnectorId(data.connector_id);
        
        // Save configuration for future use
        saveConfiguration();
        
        // Fetch courses
        fetchCourses(data.connector_id);
      } else {
        setConnectionStatus(`Connection failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Error connecting to Blackboard:', error);
      setConnectionStatus('Error connecting to Blackboard');
    }
  };

  // Fetch courses from connected Blackboard instance
  const fetchCourses = async (connId) => {
    try {
      const response = await fetch(`/api/blackboard/courses?connector_id=${connId || connectorId}`);
      const data = await response.json();
      
      if (data.success && data.courses) {
        setCourses(data.courses);
        
        // Select first course by default
        if (data.courses.length > 0) {
          setSelectedCourse(data.courses[0].id);
          fetchContentItems(connId || connectorId, data.courses[0].id);
        }
      } else {
        console.error('Failed to fetch courses:', data.error);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch content items for a selected course
  const fetchContentItems = async (connId, courseId) => {
    try {
      const response = await fetch(`/api/blackboard/course/${courseId}/content-items?connector_id=${connId || connectorId}`);
      const data = await response.json();
      
      if (data.success && data.content_items) {
        setContentItems(data.content_items);
      } else {
        console.error('Failed to fetch content items:', data.error);
      }
    } catch (error) {
      console.error('Error fetching content items:', error);
    }
  };

  // Handle course selection change
  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setSelectedCourse(courseId);
    
    if (courseId) {
      fetchContentItems(connectorId, courseId);
    } else {
      setContentItems([]);
    }
  };

  // Handle content item selection change
  const handleContentItemSelection = (contentId) => {
    setSelectedContentItems(prev => {
      if (prev.includes(contentId)) {
        return prev.filter(id => id !== contentId);
      } else {
        return [...prev, contentId];
      }
    });
  };

  // Deploy model to selected course
  const deployModel = async (modelId) => {
    if (!connectorId || !selectedCourse) {
      alert('Please connect to Blackboard and select a course first');
      return;
    }
    
    try {
      setDeploymentStatus('deploying');
      
      const response = await fetch('/api/blackboard/deploy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${connectorId}`
        },
        body: JSON.stringify({
          model_id: modelId || 'neo_algebra',
          course_id: selectedCourse,
          content_ids: selectedContentItems.length > 0 ? selectedContentItems : undefined,
          options: deploymentOptions
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setDeploymentStatus('success');
        
        // Notify parent component
        if (onDeploy) {
          onDeploy({ 
            success: true, 
            connector_id: connectorId,
            course_id: selectedCourse,
            model_id: modelId || 'neo_algebra',
            integration: data.integration
          });
        }
      } else {
        setDeploymentStatus('error');
        console.error('Deployment failed:', data.error);
      }
    } catch (error) {
      console.error('Error deploying model:', error);
      setDeploymentStatus('error');
    }
  };

  // Toggle deployment options
  const toggleOption = (option) => {
    setDeploymentOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Use a saved configuration
  const useSavedConfig = (config) => {
    setDomain(config.domain || '');
    setApiKey(config.api_key || '');
    setApiSecret(config.api_secret || '');
  };

  return (
    <div className="blackboard-integration-container">
      <div className="blackboard-header">
        <h2>Blackboard Learn Integration</h2>
        <p>Deploy Morpheus-RL AI tutors to your Blackboard Learn courses</p>
      </div>
      
      {/* Connection Section */}
      <div className="connection-section">
        <h3>Connect to Blackboard</h3>
        
        {/* Saved Configurations */}
        {savedConfigs.length > 0 && (
          <div className="saved-configs">
            <label>Saved Configurations</label>
            <div className="config-list">
              {savedConfigs.map((config, index) => (
                <button 
                  key={index} 
                  className="config-item"
                  onClick={() => {
                    // Instead of calling the hook inside a callback, use its functionality directly
                    setDomain(config.domain);
                    setApiKey(config.apiKey);
                    setApiSecret(config.apiSecret);
                    // Other state you need to set from the saved config
                  }}
                >
                  {config.domain}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Connection Form */}
        <div className="connection-form">
          <div className="form-group">
            <label htmlFor="blackboard-domain">Domain</label>
            <input
              id="blackboard-domain"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="your-school.blackboard.com"
              disabled={!!connectorId}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="blackboard-api-key">REST API Key</label>
            <input
              id="blackboard-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Your Blackboard REST API Key"
              disabled={!!connectorId}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="blackboard-api-secret">REST API Secret</label>
            <input
              id="blackboard-api-secret"
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder="Your Blackboard REST API Secret"
              disabled={!!connectorId}
            />
            <small className="help-text">
              Create REST API credentials in Blackboard Admin Panel > REST API Integrations
            </small>
          </div>
          
          <div className="connection-actions">
            <button 
              className="save-btn"
              onClick={saveConfiguration}
              disabled={!!connectorId || !domain || !apiKey || !apiSecret}
            >
              Save Configuration
            </button>
            
            <button 
              className="connect-btn"
              onClick={connectToBlackboard}
              disabled={!!connectorId || !domain || !apiKey || !apiSecret}
            >
              Connect to Blackboard
            </button>
          </div>
          
          {connectionStatus && (
            <div className={`connection-status ${connectorId ? 'success' : ''}`}>
              {connectionStatus}
            </div>
          )}
        </div>
      </div>
      
      {/* Course Selection - Only visible after connecting */}
      {connectorId && (
        <div className="course-selection-section">
          <h3>Select Course</h3>
          
          <div className="form-group">
            <label htmlFor="course-select">Course</label>
            <select 
              id="course-select" 
              value={selectedCourse} 
              onChange={handleCourseChange}
            >
              <option value="">Select a course...</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Content Item Selection */}
          {selectedCourse && contentItems.length > 0 && (
            <div className="content-item-selection">
              <h4>Content Items (Optional)</h4>
              <p className="help-text">Select specific content items to integrate with, or leave unselected to create a new content item</p>
              
              <div className="content-item-list">
                {contentItems.map(content => (
                  <div key={content.id} className="content-item">
                    <input
                      type="checkbox"
                      id={`content-${content.id}`}
                      checked={selectedContentItems.includes(content.id)}
                      onChange={() => handleContentItemSelection(content.id)}
                    />
                    <label htmlFor={`content-${content.id}`}>
                      {content.title} <span className="content-type">({content.type})</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Deployment Options */}
          {selectedCourse && (
            <div className="deployment-options">
              <h4>Deployment Options</h4>
              
              <div className="options-grid">
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-grading"
                    checked={deploymentOptions.enableGrading}
                    onChange={() => toggleOption('enableGrading')}
                  />
                  <label htmlFor="option-grading">Enable grading integration</label>
                </div>
                
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-learning-styles"
                    checked={deploymentOptions.adaptToLearningStyles}
                    onChange={() => toggleOption('adaptToLearningStyles')}
                  />
                  <label htmlFor="option-learning-styles">Adapt to student learning styles</label>
                </div>
                
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-bias"
                    checked={deploymentOptions.enableBiasReporting}
                    onChange={() => toggleOption('enableBiasReporting')}
                  />
                  <label htmlFor="option-bias">Enable bias reporting</label>
                </div>
                
                <div className="option-item">
                  <input
                    type="checkbox"
                    id="option-token"
                    checked={deploymentOptions.enableTokenEconomy}
                    onChange={() => toggleOption('enableTokenEconomy')}
                  />
                  <label htmlFor="option-token">Enable token economy</label>
                </div>
              </div>
            </div>
          )}
          
          {/* Deployment Button */}
          {selectedCourse && (
            <div className="deployment-actions">
              <button
                className={`deploy-btn ${deploymentStatus}`}
                onClick={() => deployModel()}
                disabled={deploymentStatus === 'deploying' || deploymentStatus === 'success'}
              >
                {deploymentStatus === 'deploying' ? 'Deploying...' : 
                 deploymentStatus === 'success' ? 'Deployed Successfully' : 
                 'Deploy to Blackboard'}
              </button>
              
              {deploymentStatus === 'error' && (
                <div className="deployment-error">
                  Deployment failed. Please try again or check the console for errors.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Documentation Section */}
      {!connectorId && (
        <div className="documentation-section">
          <h3>Getting Started with Blackboard Integration</h3>
          
          <div className="setup-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Enable REST API</h4>
                <p>In Blackboard, go to Admin Panel &gt; REST API Integrations and enable REST API</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Create REST API Integration</h4>
                <p>Create a new REST API integration in the admin panel</p>
                <p>Make sure to enable the required endpoints for courses, users, and content</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Get API Credentials</h4>
                <p>Save the Application ID (API Key) and Application Secret</p>
                <p>These credentials are used to authenticate with the Blackboard API</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Connect and Deploy</h4>
                <p>Enter your Blackboard domain and API credentials above to connect</p>
                <p>Once connected, select a course and deploy your model</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlackboardIntegration;